import React, { useEffect, useState, useRef } from 'react';
import numeral from 'numeral';
import CurrencyInput from 'react-currency-input-field';
import Text from '../../../../Text';
import styles from './amount.module.css';
import usePaymentStore from '../../store/use-payment-store';
import useUserContext from '../../../../../hooks/useUserContext';
import { useSoundClick } from '../../hooks/use-sound-click';

// Montos preestablecidos
const PRESET_AMOUNTS = [200, 500, 1000, 5000];

export const Amount = () => {
  const play = useSoundClick();
  const data = useUserContext();
  const maxDeposit = data?.userLoyalty?.lastLevel?.deposit_limit / 100 ?? 0;
  const { amount, setAmount, setIsInvalidAmount } = usePaymentStore();

  const minAmount = 50;
  const maxAmount = maxDeposit;
  const [selectedPreset, setSelectedPreset] = useState('otro'); // null, 'otro', or preset amount
  const inputRef = useRef(null);

  useEffect(() => {
    const isValid = amount >= minAmount && amount <= maxAmount;
    setIsInvalidAmount(!isValid);
  }, [amount, minAmount, maxAmount, setIsInvalidAmount]);

  // Verificar si el monto actual coincide con algún preset solo si no hay selección manual
  useEffect(() => {
    // Solo cambiar automáticamente si no hay una selección previa o si el monto no coincide con ningún preset
    if (selectedPreset === null) {
      if (PRESET_AMOUNTS.includes(amount)) {
        setSelectedPreset(amount);
      } else if (amount && amount !== 0) {
        setSelectedPreset('otro');
      }
    } else if (selectedPreset !== 'otro' && !PRESET_AMOUNTS.includes(amount) && amount && amount !== 0) {
      // Si estaba seleccionado un preset pero el monto cambió a algo diferente, cambiar a 'otro'
      setSelectedPreset('otro');
    }
  }, [amount, selectedPreset]);

  const handlePresetClick = (presetAmount) => {
    play();
    if (presetAmount === 'otro') {
      setSelectedPreset('otro');
      // Inicializar con '' cuando se selecciona "otro"
      setAmount('');
      // Enfocar el input automáticamente
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else {
      setSelectedPreset(presetAmount);
      setAmount(presetAmount);
    }
  };

  const handleValueChange = (value, name, values) => {
    // values.floatValue es el valor numérico sin formato
    // Si value es undefined o null, significa que el campo está vacío
    if (value === undefined || value === null || value === '') {
      setAmount('');
    } else if (values && values.floatValue !== undefined) {
      setAmount(values.floatValue);
    } else {
      // Fallback: intentar parsear el valor
      const numValue = parseFloat(value.replace(/[^0-9.]/g, ''));
      setAmount(isNaN(numValue) ? '' : numValue);
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.presetButtons}>
        {PRESET_AMOUNTS.map((presetAmount) => (
          <button
            key={presetAmount}
            className={`${styles.presetButton} ${selectedPreset === presetAmount ? styles.presetButtonActive : ''
              }`}
            onClick={() => handlePresetClick(presetAmount)}
          >
            {numeral(presetAmount).format('$0,0')}
          </button>
        ))}
        <button
          className={`${styles.presetButton} ${selectedPreset === 'otro' ? styles.presetButtonActive : ''
            }`}
          onClick={() => handlePresetClick('otro')}
        >
          Otro
        </button>
      </div>

      <div className={styles.inputContainer}>
        <div className={styles.dollarIcon}>
          <img src={require('../../assets/saldo.png')} alt="Saldo" />
        </div>
        <div className={styles.amountDisplay}>
          <CurrencyInput
            ref={inputRef}
            id="amount-input"
            name="amount"
            className={styles.input}
            value={amount || ''}
            onValueChange={handleValueChange}
            decimalsLimit={2}
            decimalSeparator="."
            groupSeparator=","
            prefix=""
            placeholder="0.00"
            allowDecimals={true}
            allowNegativeValue={false}
            autoFocus={true}
          />
        </div>
        <span className={styles.currency}>MXN</span>
      </div>

      {amount < minAmount && amount > 0 && (
        <Text center my={10} size={12} color="red">
          El monto mínimo es de {numeral(minAmount).format('$0,000')}
        </Text>
      )}
      {amount > maxAmount && (
        <Text center my={10} size={12} color="red">
          El monto máximo es de {numeral(maxAmount).format('$0,000')}
        </Text>
      )}
    </div>
  );
};
